import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Upload, Camera, User, FileText, Globe, Calendar, Briefcase, 
  Heart, MapPin, Phone, Save, Send, ChevronDown, Plus, X, Loader2,
  Users, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Country/Nationality list
const NATIONALITIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Angola', 'Argentina', 'Armenia', 'Australia',
  'Azerbaijan', 'Bahrain', 'Bangladesh', 'Belarus', 'Belgium', 'Benin', 'Bhutan',
  'Bolivia', 'Bosnia', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso',
  'Burundi', 'Cambodia', 'Cameroon', 'Canada', 'Chad', 'Chile', 'China', 'Colombia',
  'Comoros', 'Congo', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic',
  'Denmark', 'Djibouti', 'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador',
  'Eritrea', 'Estonia', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia',
  'Georgia', 'Germany', 'Ghana', 'Greece', 'Guatemala', 'Guinea', 'Haiti', 'Honduras',
  'Hong Kong', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland',
  'Israel', 'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Korea',
  'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Liberia', 'Libya', 'Lithuania',
  'Luxembourg', 'Macedonia', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali',
  'Malta', 'Mauritania', 'Mauritius', 'Mexico', 'Moldova', 'Mongolia', 'Montenegro',
  'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nepal', 'Netherlands', 'New Zealand',
  'Nicaragua', 'Niger', 'Nigeria', 'Norway', 'Oman', 'Pakistan', 'Palestine', 'Panama',
  'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar',
  'Romania', 'Russia', 'Rwanda', 'Saudi Arabia', 'Senegal', 'Serbia', 'Sierra Leone',
  'Singapore', 'Slovakia', 'Slovenia', 'Somalia', 'South Africa', 'Spain', 'Sri Lanka',
  'Sudan', 'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania',
  'Thailand', 'Togo', 'Tunisia', 'Turkey', 'Turkmenistan', 'Uganda', 'Ukraine',
  'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Venezuela', 'Vietnam',
  'Yemen', 'Zambia', 'Zimbabwe'
];

// Visa types - value is what gets sent to backend, label is what's displayed
const VISA_TYPES = [
  { value: '30_days', label: '30 Days' },
  { value: '60_days', label: '60 Days' },
  { value: '90_days', label: '90 Days' }
];
const PROFESSIONS = ['Business', 'Employee', 'Engineer', 'Doctor', 'Teacher', 'Student', 'Retired', 'Housewife', 'Other'];
const RELIGIONS = ['Islam', 'Christianity', 'Hinduism', 'Buddhism', 'Judaism', 'Sikhism', 'Other'];
const MARITAL_STATUSES = ['Single', 'Married', 'Divorced', 'Widowed'];

// Default B2B document requirements
const DEFAULT_DOC_REQUIREMENTS = [
  { id: 'passport_doc', name: 'Passport Copy', required: true },
  { id: 'personal_photo_doc', name: 'Personal Picture', required: true },
  { id: 'id_front_doc', name: 'ID Card Front', required: true },
  { id: 'id_back_doc', name: 'ID Card Back', required: false },
  { id: 'frc_mrc_doc', name: 'FRC/MRC Document', required: false }
];

const B2BApplicationNew = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [visaRates, setVisaRates] = useState({});
  const [agentInfo, setAgentInfo] = useState(null);
  const [documentRequirements, setDocumentRequirements] = useState(null);
  
  // Form Data
  const [formData, setFormData] = useState({
    // Personal Info
    first_name: '',
    middle_name: '',
    last_name: '',
    father_name: '',
    mother_name: '',
    gender: 'Male',
    date_of_birth: '',
    birth_place: '',
    birth_country: '',
    nationality: '',
    marital_status: 'Single',
    
    // Passport Info
    passport_number: '',
    date_of_issue: '',
    date_of_expiry: '',
    passport_issue_place: '',
    
    // Travel Info
    visa_duration: '30_days',
    coming_from: '',
    
    // Additional Info
    occupation: '',
    religion: '',
    address: '',
    mobile: '',
    
    // Reference
    your_reference: '',
  });

  // Documents
  const [documents, setDocuments] = useState({
    passport_doc: null,
    personal_photo_doc: null,
    id_front_doc: null,
    id_back_doc: null,
    frc_mrc_doc: null,
    other_docs: null,
  });

  // Document previews
  const [previews, setPreviews] = useState({
    passport_doc: null,
    personal_photo_doc: null,
    id_front_doc: null,
    id_back_doc: null,
    frc_mrc_doc: null,
    other_docs: null,
  });

  // Done Base Options
  const [isDoneBase, setIsDoneBase] = useState(false);
  const [doneBaseType, setDoneBaseType] = useState('none');
  
  // Family Members
  const [familyMembers, setFamilyMembers] = useState([]);
  const [showFamilyForm, setShowFamilyForm] = useState(false);

  // Get current document requirements based on nationality
  const getCurrentDocRequirements = useCallback(() => {
    if (formData.nationality && documentRequirements?.by_nationality?.[formData.nationality]) {
      return documentRequirements.by_nationality[formData.nationality];
    }
    return documentRequirements?.default || DEFAULT_DOC_REQUIREMENTS;
  }, [formData.nationality, documentRequirements]);

  // Get token
  const getToken = () => localStorage.getItem('agent_token');

  // Fetch agent info and rates
  useEffect(() => {
    const fetchData = async () => {
      const token = getToken();
      if (!token) {
        navigate('/b2b-agent');
        return;
      }

      try {
        // Fetch agent profile
        const profileRes = await axios.get(`${API}/b2b/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAgentInfo(profileRes.data);
        setWalletBalance(profileRes.data.wallet_balance || 0);

        // Fetch rates
        const ratesRes = await axios.get(`${API}/b2b/rates`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Store rates properly
        if (ratesRes.data && ratesRes.data.rates) {
          setVisaRates(ratesRes.data.rates);
        }

        // Fetch document requirements
        try {
          const docReqRes = await axios.get(`${API}/b2b/document-requirements`);
          setDocumentRequirements(docReqRes.data);
        } catch (docError) {
          console.log('Using default document requirements');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        if (error.response?.status === 401) {
          navigate('/b2b-agent');
        }
      }
    };

    fetchData();
  }, [navigate]);

  // Get rate for selected nationality and visa type
  const getRate = useCallback(() => {
    if (!formData.nationality || !visaRates[formData.nationality]) return 0;
    const nationalityRates = visaRates[formData.nationality];
    
    // Handle nested rates structure: { rates: { 30_days: 400, 60_days: 600 } }
    const rates = nationalityRates.rates || nationalityRates;
    
    // visa_duration is now in format "30_days", "60_days", etc.
    return rates[formData.visa_duration] || 0;
  }, [formData.nationality, formData.visa_duration, visaRates]);

  // Calculate total fee
  const getTotalFee = useCallback(() => {
    const baseRate = getRate();
    const familyCount = familyMembers.length;
    return baseRate * (1 + familyCount);
  }, [getRate, familyMembers.length]);

  // Handle form change
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle file upload
  const handleFileUpload = (field, file) => {
    if (!file) return;
    
    setDocuments(prev => ({ ...prev, [field]: file }));
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviews(prev => ({ ...prev, [field]: e.target.result }));
    };
    reader.readAsDataURL(file);
  };

  // Add family member
  const addFamilyMember = () => {
    setFamilyMembers(prev => [...prev, {
      id: Date.now(),
      first_name: '',
      middle_name: '',
      last_name: '',
      passport_number: '',
      date_of_birth: '',
      date_of_issue: '',
      date_of_expiry: '',
      relation: 'Spouse',
      // Document files for each family member
      passport_doc: null,
      id_front_doc: null,
      id_back_doc: null,
      frc_mrc_doc: null,
    }]);
    setShowFamilyForm(true);
  };

  // Update family member
  const updateFamilyMember = (id, field, value) => {
    setFamilyMembers(prev => prev.map(m => 
      m.id === id ? { ...m, [field]: value } : m
    ));
  };

  // Remove family member
  const removeFamilyMember = (id) => {
    setFamilyMembers(prev => prev.filter(m => m.id !== id));
  };

  // Submit form
  const handleSubmit = async (saveAsDraft = false) => {
    const token = getToken();
    if (!token) {
      toast.error('Please login first');
      navigate('/b2b-agent');
      return;
    }

    // Validation
    if (!formData.first_name || !formData.last_name || !formData.passport_number) {
      toast.error('Please fill in required fields (First Name, Last Name, Passport Number)');
      return;
    }

    if (!formData.nationality) {
      toast.error('Please select nationality');
      return;
    }

    if (!formData.date_of_birth || !formData.date_of_issue || !formData.date_of_expiry) {
      toast.error('Please fill in all date fields (DOB, Issue Date, Expiry Date)');
      return;
    }

    if (!formData.occupation) {
      toast.error('Please select a profession/occupation');
      return;
    }

    // Dynamic document validation based on requirements - SKIP for drafts
    if (!saveAsDraft) {
      const currentRequirements = getCurrentDocRequirements();
      for (const doc of currentRequirements) {
        if (doc.required && !documents[doc.id]) {
          toast.error(`Please upload ${doc.name}`);
          return;
        }
      }
    }

    // Balance check (skip for drafts and done base)
    if (!saveAsDraft && !isDoneBase && getTotalFee() > walletBalance) {
      toast.error(`Insufficient balance. Required: ${getTotalFee()} AED`);
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      
      // Add form fields
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });

      // Add documents dynamically based on what's uploaded
      if (documents.passport_doc) formDataToSend.append('passport_doc', documents.passport_doc);
      if (documents.personal_photo_doc) formDataToSend.append('personal_photo_doc', documents.personal_photo_doc);
      if (documents.id_front_doc) formDataToSend.append('id_front_doc', documents.id_front_doc);
      if (documents.id_back_doc) formDataToSend.append('id_back_doc', documents.id_back_doc);
      if (documents.frc_mrc_doc) formDataToSend.append('frc_mrc_doc', documents.frc_mrc_doc);
      if (documents.other_docs) formDataToSend.append('supporting_docs', documents.other_docs);

      // Add flags
      formDataToSend.append('is_done_base', isDoneBase.toString());
      formDataToSend.append('done_base_type', doneBaseType);
      formDataToSend.append('save_as_draft', saveAsDraft.toString());

      // Add family data if any
      if (familyMembers.length > 0) {
        formDataToSend.append('family_data', JSON.stringify(familyMembers.map(m => ({
          first_name: m.first_name,
          middle_name: m.middle_name,
          last_name: m.last_name,
          passport_number: m.passport_number,
          date_of_birth: m.date_of_birth,
          date_of_issue: m.date_of_issue,
          date_of_expiry: m.date_of_expiry,
          relation: m.relation,
        }))));
        
        // Add family files in the order backend expects: passport, id_front, id_back, frc_mrc
        // Each member's files must be added in sequence
        familyMembers.forEach((member, index) => {
          // passport (required)
          if (member.passport_doc) {
            formDataToSend.append('family_files', member.passport_doc);
          } else {
            formDataToSend.append('family_files', new Blob([''], {type: 'text/plain'}), 'empty');
          }
          // id_front (required)
          if (member.id_front_doc) {
            formDataToSend.append('family_files', member.id_front_doc);
          } else {
            formDataToSend.append('family_files', new Blob([''], {type: 'text/plain'}), 'empty');
          }
          // id_back (required)
          if (member.id_back_doc) {
            formDataToSend.append('family_files', member.id_back_doc);
          } else {
            formDataToSend.append('family_files', new Blob([''], {type: 'text/plain'}), 'empty');
          }
          // frc_mrc (optional)
          if (member.frc_mrc_doc) {
            formDataToSend.append('family_files', member.frc_mrc_doc);
          } else {
            formDataToSend.append('family_files', new Blob([''], {type: 'text/plain'}), 'empty');
          }
        });
      }

      const response = await axios.post(`${API}/b2b/applications`, formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.is_draft) {
        toast.success('Application saved as draft!');
      } else if (response.data.payment_deferred) {
        toast.success('Done Base application submitted!');
      } else {
        toast.success('Application submitted successfully!');
      }

      navigate('/b2b-agent/dashboard');
    } catch (error) {
      console.error('Submit error:', error);
      console.error('Error response:', error.response?.data);
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
      } else if (Array.isArray(errorDetail)) {
        // FastAPI validation errors come as array
        const messages = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        toast.error(messages || 'Validation error');
      } else {
        toast.error('Failed to submit application');
      }
    } finally {
      setLoading(false);
    }
  };

  // Document upload component
  const DocumentUpload = ({ label, field, required = false }) => (
    <div className="space-y-2">
      <Label className="text-xs font-medium text-slate-600">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      <div 
        className={`relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors
          ${previews[field] ? 'border-green-400 bg-green-50' : 'border-slate-300 hover:border-blue-400 hover:bg-blue-50'}`}
        onClick={() => document.getElementById(`file-${field}`).click()}
      >
        {previews[field] ? (
          <div className="relative">
            <img src={previews[field]} alt={label} className="max-h-32 mx-auto rounded" />
            <Button
              size="sm"
              variant="destructive"
              className="absolute top-0 right-0 h-6 w-6 p-0"
              onClick={(e) => {
                e.stopPropagation();
                setDocuments(prev => ({ ...prev, [field]: null }));
                setPreviews(prev => ({ ...prev, [field]: null }));
              }}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ) : (
          <div className="py-4">
            <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
            <p className="text-xs text-slate-500">Click to upload</p>
          </div>
        )}
        <input
          id={`file-${field}`}
          type="file"
          className="hidden"
          accept="image/*,.pdf"
          onChange={(e) => handleFileUpload(field, e.target.files[0])}
        />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-800">New Visa Application</h1>
            <p className="text-sm text-slate-500">Dubai Tourist Visa</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-xs text-slate-500">Wallet Balance</p>
              <p className="text-lg font-bold text-green-600">{walletBalance.toFixed(2)} AED</p>
            </div>
            <Button variant="outline" onClick={() => navigate('/b2b-agent/dashboard')}>
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Left Column - Main Form */}
          <div className="col-span-12 lg:col-span-8">
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Applicant Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Visa Type & Reference Row */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">Visa Type <span className="text-red-500">*</span></Label>
                    <Select value={formData.visa_duration} onValueChange={(v) => handleChange('visa_duration', v)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {VISA_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Nationality <span className="text-red-500">*</span></Label>
                    <Select value={formData.nationality} onValueChange={(v) => handleChange('nationality', v)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {NATIONALITIES.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Your Reference</Label>
                    <Input 
                      className="mt-1"
                      value={formData.your_reference}
                      onChange={(e) => handleChange('your_reference', e.target.value)}
                      placeholder="Booking ID, etc."
                    />
                  </div>
                </div>

                {/* Passport Info */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">Passport No. <span className="text-red-500">*</span></Label>
                    <Input 
                      className="mt-1"
                      value={formData.passport_number}
                      onChange={(e) => handleChange('passport_number', e.target.value.toUpperCase())}
                      placeholder="AB1234567"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Date of Issue <span className="text-red-500">*</span></Label>
                    <DatePicker 
                      className="mt-1 w-full"
                      value={formData.date_of_issue}
                      onChange={(val) => handleChange('date_of_issue', val)}
                      placeholder="Select issue date"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Expiration Date <span className="text-red-500">*</span></Label>
                    <DatePicker 
                      className="mt-1 w-full"
                      value={formData.date_of_expiry}
                      onChange={(val) => handleChange('date_of_expiry', val)}
                      placeholder="Select expiry date"
                    />
                  </div>
                </div>

                {/* Gender & DOB */}
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label className="text-xs">Gender <span className="text-red-500">*</span></Label>
                    <div className="flex gap-4 mt-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                          type="radio" 
                          name="gender" 
                          checked={formData.gender === 'Male'}
                          onChange={() => handleChange('gender', 'Male')}
                          className="text-blue-600"
                        />
                        <span className="text-sm">Male</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                          type="radio" 
                          name="gender" 
                          checked={formData.gender === 'Female'}
                          onChange={() => handleChange('gender', 'Female')}
                          className="text-blue-600"
                        />
                        <span className="text-sm">Female</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Date of Birth <span className="text-red-500">*</span></Label>
                    <DatePicker 
                      className="mt-1 w-full"
                      value={formData.date_of_birth}
                      onChange={(val) => handleChange('date_of_birth', val)}
                      placeholder="Select birth date"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Birth Place</Label>
                    <Input 
                      className="mt-1"
                      value={formData.birth_place}
                      onChange={(e) => handleChange('birth_place', e.target.value)}
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Marital Status</Label>
                    <Select value={formData.marital_status} onValueChange={(v) => handleChange('marital_status', v)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MARITAL_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Names */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">First Name <span className="text-red-500">*</span></Label>
                    <Input 
                      className="mt-1"
                      value={formData.first_name}
                      onChange={(e) => handleChange('first_name', e.target.value.toUpperCase())}
                      placeholder="JOHN"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Middle Name</Label>
                    <Input 
                      className="mt-1"
                      value={formData.middle_name}
                      onChange={(e) => handleChange('middle_name', e.target.value.toUpperCase())}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Last Name <span className="text-red-500">*</span></Label>
                    <Input 
                      className="mt-1"
                      value={formData.last_name}
                      onChange={(e) => handleChange('last_name', e.target.value.toUpperCase())}
                      placeholder="DOE"
                    />
                  </div>
                </div>

                {/* Parent Names */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs">Father Name</Label>
                    <Input 
                      className="mt-1"
                      value={formData.father_name}
                      onChange={(e) => handleChange('father_name', e.target.value.toUpperCase())}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Mother Name</Label>
                    <Input 
                      className="mt-1"
                      value={formData.mother_name}
                      onChange={(e) => handleChange('mother_name', e.target.value.toUpperCase())}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Coming From</Label>
                    <Input 
                      className="mt-1"
                      value={formData.coming_from}
                      onChange={(e) => handleChange('coming_from', e.target.value)}
                      placeholder="Country"
                    />
                  </div>
                </div>

                {/* Additional Info */}
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label className="text-xs">Profession</Label>
                    <Select value={formData.occupation} onValueChange={(v) => handleChange('occupation', v)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {PROFESSIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Religion</Label>
                    <Select value={formData.religion} onValueChange={(v) => handleChange('religion', v)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {RELIGIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Passport Issue Place</Label>
                    <Input 
                      className="mt-1"
                      value={formData.passport_issue_place}
                      onChange={(e) => handleChange('passport_issue_place', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Mobile</Label>
                    <Input 
                      className="mt-1"
                      value={formData.mobile}
                      onChange={(e) => handleChange('mobile', e.target.value)}
                      placeholder="+971..."
                    />
                  </div>
                </div>

                {/* Done Base Option */}
                <div className="border-t pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Checkbox 
                      id="done-base"
                      checked={isDoneBase}
                      onCheckedChange={setIsDoneBase}
                    />
                    <Label htmlFor="done-base" className="text-sm font-medium cursor-pointer">
                      Done Base (Pay on Approval)
                    </Label>
                  </div>
                  {isDoneBase && (
                    <div className="ml-6">
                      <Select value={doneBaseType} onValueChange={setDoneBaseType}>
                        <SelectTrigger className="w-64">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Single Application</SelectItem>
                          <SelectItem value="family">Family (with members)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Family Members Section - Available for both regular and Done Base family applications */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Family Members ({familyMembers.length})
                    </h3>
                    <Button size="sm" variant="outline" onClick={addFamilyMember}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Family Member
                    </Button>
                  </div>
                  
                  {familyMembers.length === 0 && (
                    <p className="text-sm text-slate-500 mb-4">
                      No family members added. Click "Add Family Member" to include family in this application.
                    </p>
                  )}
                  
                  {familyMembers.map((member, index) => (
                    <div key={member.id} className="border rounded-lg p-4 mb-3 bg-slate-50">
                      <div className="flex justify-between items-center mb-3">
                        <Badge variant="outline">Family Member {index + 1}</Badge>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-6 w-6 p-0 text-red-500"
                          onClick={() => removeFamilyMember(member.id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-4 gap-3">
                        <Input 
                          placeholder="First Name"
                          value={member.first_name}
                          onChange={(e) => updateFamilyMember(member.id, 'first_name', e.target.value.toUpperCase())}
                        />
                        <Input 
                          placeholder="Last Name"
                          value={member.last_name}
                          onChange={(e) => updateFamilyMember(member.id, 'last_name', e.target.value.toUpperCase())}
                        />
                        <Input 
                          placeholder="Passport No."
                          value={member.passport_number}
                          onChange={(e) => updateFamilyMember(member.id, 'passport_number', e.target.value.toUpperCase())}
                        />
                        <Select 
                          value={member.relation} 
                          onValueChange={(v) => updateFamilyMember(member.id, 'relation', v)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Spouse">Spouse</SelectItem>
                            <SelectItem value="Child">Child</SelectItem>
                            <SelectItem value="Parent">Parent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-3 gap-3 mt-3">
                        <div>
                          <Label className="text-xs">Date of Birth</Label>
                          <DatePicker 
                            className="w-full"
                            value={member.date_of_birth}
                            onChange={(val) => updateFamilyMember(member.id, 'date_of_birth', val)}
                            placeholder="DOB"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Passport Issue</Label>
                          <DatePicker 
                            className="w-full"
                            value={member.date_of_issue}
                            onChange={(val) => updateFamilyMember(member.id, 'date_of_issue', val)}
                            placeholder="Issue date"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Passport Expiry</Label>
                          <DatePicker 
                            className="w-full"
                            value={member.date_of_expiry}
                            onChange={(val) => updateFamilyMember(member.id, 'date_of_expiry', val)}
                            placeholder="Expiry date"
                          />
                        </div>
                      </div>
                      {/* Family Member Documents */}
                      <div className="mt-4 pt-3 border-t">
                        <Label className="text-xs font-medium text-slate-600 mb-2 block">Documents for {member.first_name || 'this member'}</Label>
                        <div className="grid grid-cols-4 gap-2">
                          <div>
                            <Label className="text-xs text-slate-500">Passport <span className="text-red-500">*</span></Label>
                            <Input 
                              type="file"
                              accept="image/*,.pdf"
                              className="mt-1 text-xs"
                              onChange={(e) => updateFamilyMember(member.id, 'passport_doc', e.target.files[0])}
                            />
                            {member.passport_doc && <p className="text-xs text-green-600 mt-1 truncate">{member.passport_doc.name}</p>}
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">ID Front <span className="text-red-500">*</span></Label>
                            <Input 
                              type="file"
                              accept="image/*,.pdf"
                              className="mt-1 text-xs"
                              onChange={(e) => updateFamilyMember(member.id, 'id_front_doc', e.target.files[0])}
                            />
                            {member.id_front_doc && <p className="text-xs text-green-600 mt-1 truncate">{member.id_front_doc.name}</p>}
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">ID Back <span className="text-red-500">*</span></Label>
                            <Input 
                              type="file"
                              accept="image/*,.pdf"
                              className="mt-1 text-xs"
                              onChange={(e) => updateFamilyMember(member.id, 'id_back_doc', e.target.files[0])}
                            />
                            {member.id_back_doc && <p className="text-xs text-green-600 mt-1 truncate">{member.id_back_doc.name}</p>}
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">FRC/MRC</Label>
                            <Input 
                              type="file"
                              accept="image/*,.pdf"
                              className="mt-1 text-xs"
                              onChange={(e) => updateFamilyMember(member.id, 'frc_mrc_doc', e.target.files[0])}
                            />
                            {member.frc_mrc_doc && <p className="text-xs text-green-600 mt-1 truncate">{member.frc_mrc_doc.name}</p>}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Documents & Rate */}
          <div className="col-span-12 lg:col-span-4 space-y-6">
            {/* Rate Card */}
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Application Rate</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Visa Type:</span>
                    <span className="font-medium">{VISA_TYPES.find(t => t.value === formData.visa_duration)?.label || formData.visa_duration}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Nationality:</span>
                    <span className="font-medium">{formData.nationality || '-'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Rate per person:</span>
                    <span className="font-medium">{getRate()} AED</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Family Members:</span>
                    <span className="font-medium">{familyMembers.length}</span>
                  </div>
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total:</span>
                      <span className="text-blue-600">{getTotalFee()} AED</span>
                    </div>
                  </div>
                  {isDoneBase && (
                    <p className="text-xs text-amber-600 mt-2">
                      <AlertCircle className="h-3 w-3 inline mr-1" />
                      Payment deferred until approval
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Documents */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Documents
                </CardTitle>
                {formData.nationality && (
                  <p className="text-xs text-slate-500 mt-1">
                    Document requirements for {formData.nationality}
                  </p>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {getCurrentDocRequirements().map((doc) => (
                  <DocumentUpload 
                    key={doc.id}
                    label={doc.name} 
                    field={doc.id} 
                    required={doc.required}
                  />
                ))}
                <DocumentUpload label="Other Documents" field="other_docs" />
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button 
                className="w-full bg-green-600 hover:bg-green-700 h-12 text-lg"
                onClick={() => handleSubmit(false)}
                disabled={loading || (!isDoneBase && getTotalFee() > walletBalance)}
              >
                {loading ? (
                  <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Processing...</>
                ) : isDoneBase ? (
                  <><Send className="h-5 w-5 mr-2" /> Submit Done Base</>
                ) : (
                  <><Send className="h-5 w-5 mr-2" /> Submit & Pay {getTotalFee()} AED</>
                )}
              </Button>
              
              <Button 
                variant="outline"
                className="w-full h-10 border-purple-400 text-purple-600 hover:bg-purple-50"
                onClick={() => handleSubmit(true)}
                disabled={loading}
              >
                <Save className="h-4 w-4 mr-2" />
                Save as Draft
              </Button>

              {!isDoneBase && getTotalFee() > walletBalance && (
                <p className="text-xs text-red-500 text-center">
                  <AlertCircle className="h-3 w-3 inline mr-1" />
                  Insufficient balance. Need {(getTotalFee() - walletBalance).toFixed(2)} AED more.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default B2BApplicationNew;
